/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useRef, useState } from 'react';
import {
  Button,
  Grow,
  Paper,
  Popper,
  MenuItem,
  MenuList,
  ButtonGroup,
  ClickAwayListener,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
/**
 * Implementation of split button with dropdown options
 * @param {Array} options - Options for dropdown menu
 * @param {Function} handleClick - Function to trigger when the button is clicked
 * @param {String} color - Button background color
 * @param {String} type - Button action type
 * @returns
 */
const SplitButton = ({
  options,
  handleClick,
  selectedIndex,
  setSelectedIndex,
  color = 'primary',
  type = 'submit',
  disabled = false,
  tabName = 'tab',
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  //
  const handleMenuItemClick = (event, index) => {
    handleClick(selectedIndex);
    setSelectedIndex(index);
    setOpen(false);
  };
  //
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };
  //
  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };
  //
  return (
    <>
      <ButtonGroup variant="contained" ref={anchorRef} aria-label="split button" color={color}>
        <Button
          size="small"
          onClick={() => handleClick(selectedIndex)}
          sx={{ width: 110, textTransform: 'uppercase' }}
          type={type}
          disabled={disabled}
          id={`auto-${tabName}-save-${selectedIndex}`}
        >
          {options[selectedIndex]}
        </Button>
        <Button
          size="small"
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label="select merge strategy"
          aria-haspopup="menu"
          onClick={handleToggle}
          disabled={disabled}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper
        sx={{
          zIndex: 1,
        }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu" autoFocusItem>
                  {options.map((option, index) => (
                    <MenuItem
                      key={option}
                      selected={index === selectedIndex}
                      onClick={(event) => handleMenuItemClick(event, index)}
                    >
                      {option}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};
//
export default SplitButton;
